// Square north-up minimap: heightmap backdrop, persistent movement trail,
// and a heading arrow that points in the direction of travel.
export class Minimap {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private img: HTMLImageElement | null = null;
  private period: number;
  private playerX = 0;
  private playerZ = 0;
  private heading = 0;
  private trail: [number, number][] = [];

  constructor(period: number, imageUrl: string) {
    this.period = period;
    this.canvas = document.createElement('canvas');
    this.canvas.width = 176;
    this.canvas.height = 176;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');
    this.ctx = ctx;

    const img = new Image();
    img.onload = () => {
      this.img = img;
    };
    img.src = imageUrl;
  }

  update(x: number, z: number, heading: number): void {
    this.playerX = x;
    this.playerZ = z;
    this.heading = heading;

    const period = this.period;
    const px = ((x % period) + period) % period;
    const pz = ((z % period) + period) % period;
    const last = this.trail[this.trail.length - 1];
    // Trail is never trimmed — it persists for the whole session.
    if (!last || Math.hypot(px - last[0], pz - last[1]) > 8) {
      this.trail.push([px, pz]);
    }
  }

  render(): void {
    const ctx = this.ctx;
    const S = this.canvas.width;
    const period = this.period;

    ctx.clearRect(0, 0, S, S);

    if (this.img) {
      ctx.drawImage(this.img, 0, 0, S, S);
    } else {
      ctx.fillStyle = '#1a2740';
      ctx.fillRect(0, 0, S, S);
    }

    const toScreen = (wx: number, wz: number): [number, number] => [
      (wx / period) * S,
      (wz / period) * S,
    ];

    // Trail (persisted). Break the stroke across the toroidal seam so it
    // doesn't draw a line zipping across the whole map on wrap.
    ctx.strokeStyle = 'rgba(255,210,80,0.6)';
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    let prev: [number, number] | null = null;
    for (let i = 0; i < this.trail.length; i++) {
      const p = toScreen(this.trail[i][0], this.trail[i][1]);
      if (prev === null) {
        ctx.moveTo(p[0], p[1]);
      } else if (Math.hypot(p[0] - prev[0], p[1] - prev[1]) > S / 2) {
        ctx.moveTo(p[0], p[1]); // seam wrap — start a new sub-path
      } else {
        ctx.lineTo(p[0], p[1]);
      }
      prev = p;
    }
    ctx.stroke();

    // Player heading arrow. The arrow is drawn pointing up; on this north-up
    // map (world +x right, +z down) the canvas rotation for a world heading
    // atan2(vx,vz) is (PI - heading).
    const [px, py] = toScreen(
      ((this.playerX % period) + period) % period,
      ((this.playerZ % period) + period) % period,
    );
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(Math.PI - this.heading);
    ctx.beginPath();
    ctx.moveTo(0, -7);
    ctx.lineTo(5, 6);
    ctx.lineTo(0, 3);
    ctx.lineTo(-5, 6);
    ctx.closePath();
    ctx.fillStyle = '#ffd23c';
    ctx.strokeStyle = '#1b1b1b';
    ctx.lineWidth = 1.5;
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Square border
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, S - 2, S - 2);
  }
}
